<script setup lang="ts">
import { computed, PropType } from "vue";
import {
    BannerViewer,
    matchesBannerAudience,
    NotificationBannerItem,
    resolveBannerAudience,
} from "@persolog/utils";

const props = defineProps({
    banners: {
        type: Array as PropType<NotificationBannerItem[]>,
        default: () => [],
    },
    // Interface language of the (possibly anonymous) viewer, e.g. "de".
    locale: {
        type: String,
        default: "en",
    },
    // Logged-in trainer, or null before login / for anonymous visitors.
    trainer: {
        type: Object as PropType<BannerViewer | null>,
        default: null,
    },
});

const viewerAudience = computed(() => resolveBannerAudience(props.locale, props.trainer));

// Several banners can be due at once; they stack rather than compete, and none of them is
// dismissible — a banner is up for as long as its display window says so (RDT-339).
const visibleBanners = computed(() =>
    props.banners.filter((banner) => matchesBannerAudience(banner.audience, viewerAudience.value)),
);
</script>

<template>
    <div v-if="visibleBanners.length" id="persolog-notification-banner">
        <div
            v-for="(banner, index) in visibleBanners"
            :key="banner.id"
            class="banner"
            data-cy="notification-banner"
        >
            <div class="text-center text-white info-banner-content">
                <!-- The first banner keeps the stable `banner-text` id the Cypress specs assert on;
                     ids must stay unique, so the rest are addressed by data-cy. -->
                <span :id="index === 0 ? 'banner-text' : undefined" data-cy="banner-text" v-html="banner.text"></span>
            </div>
        </div>
    </div>
</template>

<style scoped>
/*
 * The component carries its own layout. It used to lean on two classes it does not own: `p-menubar`
 * for the padding and background — a PrimeVue COMPONENT class, which also made the row a flex
 * container — and `.info-banner-content`, which is declared in profile-administration's and
 * profile-download's own app css. Those two apps therefore gave the inner box `width: 100%` and it
 * filled the flex row; the questionnaire has neither rule, so the box shrank to its text and sat at
 * the left edge (RDT-339). The values below reproduce what those apps were rendering.
 */
.banner {
    border-radius: 0;
    border: 0;
    width: 100%;
    padding: 1rem 0.75rem 0.5rem;
    background-color: var(--p-secondary-500);
}

.info-banner-content {
    width: 100%;
    max-width: 1500px;
    margin: 0 auto;
    padding-left: 2rem;
    padding-right: 2rem;
}
</style>
